import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { enablePush, getPushPermission, pushSupported } from "@/lib/push";

/**
 * Shown once per login when the user hasn't decided on notifications yet
 * (permission === "default"). Mounts with the authenticated app, so it reappears
 * on the next login while the decision is still pending. "Enable" fires the real
 * browser permission prompt from this user gesture (required by Safari/Firefox).
 */
export const NotificationPrompt = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pushSupported() && getPushPermission() === "default") setOpen(true);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      if (res?.subscribed) toast.success("Notifications enabled");
      else if (res?.permission === "denied")
        toast.error("Notifications are blocked in your browser settings");
    } catch {
      toast.error("Could not enable notifications");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[400px] rounded-[18px]">
        <DialogHeader>
          <div className="mx-auto mb-1 grid h-12 w-12 place-items-center rounded-full bg-brand-gold-soft">
            <Bell className="text-brand-gold-deep" size={22} />
          </div>
          <DialogTitle className="brand-title text-center text-xl">
            Turn on notifications?
          </DialogTitle>
        </DialogHeader>
        <p className="text-center text-sm text-brand-muted">
          Get instant alerts about your referrals, rewards, and payouts — even
          when the app is closed.
        </p>
        <div className="mt-2 flex gap-2">
          <Button
            variant="outline"
            className="h-11 flex-1 rounded-[12px]"
            onClick={() => setOpen(false)}
          >
            Not now
          </Button>
          <Button
            disabled={busy}
            onClick={enable}
            className="h-11 flex-1 rounded-[12px] bg-brand-forest font-semibold text-white"
          >
            Enable
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
